import React, { useMemo, useState } from 'react'
import { scaleDiverging, scaleLinear } from 'd3-scale'
import { interpolateRdBu } from 'd3-scale-chromatic'
import {
  extractFeatureMatrix,
  normalizeFeatures,
  computePCA,
  computeClusterProfiles,
  generateClusterLabels,
  computeDistanceMatrix,
  hierarchicalClustering,
  FEATURE_META,
  CLUSTERING_FEATURES,
  CATEGORY_COLORS,
  CLUSTER_COLORS
} from '../lib/clustering'

/**
 * Cluster count selector
 */
const ClusterSelector = ({ k, onChange, maxK }) => (
  <div className="flex items-center gap-2">
    <label className="text-sm text-gray-500 mb-0">Número de grupos:</label>
    <input
      type="range"
      className="h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
      style={{ width: 100 }}
      min={2}
      max={maxK}
      value={k}
      onChange={(e) => onChange(parseInt(e.target.value))}
    />
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-500 text-white">{k}</span>
  </div>
)

/**
 * Generate human-readable cluster description
 * Always shows the most distinctive features, even if differences are small
 */
function generateClusterDescription(profile, globalMeans, featureNames) {
  // Calculate all differences
  const allDiffs = featureNames.map((feature, i) => {
    const diff = profile[i] - globalMeans[i]
    const meta = FEATURE_META[feature]
    return { feature, label: meta?.label || feature, diff, absDiff: Math.abs(diff) }
  }).filter(d => d.label)

  // Sort by absolute difference
  allDiffs.sort((a, b) => b.absDiff - a.absDiff)

  // Take top 4 most distinctive features
  const top = allDiffs.slice(0, 4)

  const high = top.filter(d => d.diff > 0).slice(0, 2)
  const low = top.filter(d => d.diff < 0).slice(0, 2)

  const traits = []

  // Use different language based on magnitude
  if (high.length > 0) {
    const prefix = high[0].absDiff > 15 ? 'Alto' : 'Mais'
    traits.push(`${prefix}: ${high.map(d => d.label).join(', ')}`)
  }
  if (low.length > 0) {
    const prefix = low[0].absDiff > 15 ? 'Baixo' : 'Menos'
    traits.push(`${prefix}: ${low.map(d => d.label).join(', ')}`)
  }

  return traits.length > 0 ? traits.join(' · ') : 'Perfil balanceado'
}

/**
 * Cluster summary cards - shows ALL polos in each cluster
 */
const ClusterSummary = ({ profiles, globalMeans, featureNames, clusterCounts, polos, assignments, canView = false }) => {
  return (
    <div className="grid grid-cols-1 gap-3 mb-4">
      {profiles.map((profile, i) => {
        const description = generateClusterDescription(profile, globalMeans, featureNames)
        const clusterPolos = polos.filter((_, idx) => assignments[idx] === i)

        return (
          <div key={i} className="w-full">
            <div
              className="bg-white rounded-lg shadow-sm border-0"
              style={{ borderLeft: `4px solid ${CLUSTER_COLORS[i]}` }}
            >
              <div className="p-3">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <span className="font-bold text-base" style={{ color: CLUSTER_COLORS[i] }}>
                      Grupo {i + 1}
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 ml-2">
                      {clusterCounts[i]} polos
                    </span>
                  </div>
                  <div className="text-sm text-gray-500">{description}</div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {clusterPolos.map((p, idx) => {
                    const style = {
                      backgroundColor: `${CLUSTER_COLORS[i]}22`,
                      color: CLUSTER_COLORS[i],
                      border: `1px solid ${CLUSTER_COLORS[i]}44`
                    }
                    const className = "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium no-underline"
                    return canView ? (
                      <a key={p.id} href={`/polos/${p.id}`} className={className} style={style}>
                        {p.nome}
                      </a>
                    ) : (
                      <span
                        key={p.id}
                        className={className}
                        style={style}
                        title="Solicite autorização para visualizar este polo"
                      >
                        {p.nome}
                      </span>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

/**
 * Dendrogram + Heatmap View
 */
const DendrogramHeatmap = ({ polos, normalizedData, assignments, order, featureNames, k }) => {
  const [hoveredCell, setHoveredCell] = useState(null)

  const cellSize = 14
  const labelWidth = 140
  const featureLabelHeight = 100
  const margin = { top: featureLabelHeight, left: labelWidth, right: 60, bottom: 20 }

  const orderedPolos = order.map(i => polos[i])
  const orderedData = order.map(i => normalizedData[i])
  const orderedAssignments = order.map(i => assignments[i])

  const width = margin.left + featureNames.length * cellSize + margin.right
  const height = margin.top + polos.length * cellSize + margin.bottom

  const colorScale = scaleDiverging(t => interpolateRdBu(1 - t)).domain([-2, 0, 2])

  return (
    <div>
      <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded px-3 py-2 text-sm mb-3">
        <i className="fa fa-lightbulb-o mr-2" />
        <strong>Como ler:</strong> Cada linha é um polo, cada coluna é uma característica.
        Cores <span className="text-blue-600 font-bold">azuis</span> = abaixo da média,
        cores <span className="text-red-600 font-bold">vermelhas</span> = acima da média.
        A faixa colorida à direita mostra o grupo de cada polo.
        Polos estão ordenados por similaridade — vizinhos têm características parecidas.
      </div>
      <div className="overflow-auto">
        <svg width={width} height={height}>
          {/* Feature labels (top) */}
        {featureNames.map((feature, j) => {
          const meta = FEATURE_META[feature]
          return (
            <g key={`feature-${j}`} transform={`translate(${margin.left + j * cellSize + cellSize / 2}, ${margin.top - 5})`}>
              <text
                transform="rotate(-65)"
                textAnchor="start"
                fontSize={9}
                fill={CATEGORY_COLORS[meta.category]}
              >
                {meta.label}
              </text>
            </g>
          )
        })}

        {/* Polo labels (left) */}
        {orderedPolos.map((polo, i) => (
          <g key={`polo-${i}`} transform={`translate(${margin.left - 5}, ${margin.top + i * cellSize + cellSize / 2})`}>
            <text
              textAnchor="end"
              dominantBaseline="middle"
              fontSize={10}
              fill="#333"
            >
              {polo.nome.length > 18 ? polo.nome.slice(0, 18) + '...' : polo.nome}
            </text>
          </g>
        ))}

        {/* Cluster indicators (right) */}
        {orderedPolos.map((polo, i) => (
          <rect
            key={`cluster-${i}`}
            x={margin.left + featureNames.length * cellSize + 5}
            y={margin.top + i * cellSize}
            width={20}
            height={cellSize}
            fill={CLUSTER_COLORS[orderedAssignments[i] % CLUSTER_COLORS.length]}
            rx={2}
          />
        ))}

        {/* Heatmap cells */}
        {orderedData.map((row, i) =>
          row.map((value, j) => (
            <rect
              key={`cell-${i}-${j}`}
              x={margin.left + j * cellSize}
              y={margin.top + i * cellSize}
              width={cellSize - 1}
              height={cellSize - 1}
              fill={colorScale(value)}
              onMouseEnter={() => setHoveredCell({ polo: orderedPolos[i], feature: featureNames[j], value, row: i, col: j })}
              onMouseLeave={() => setHoveredCell(null)}
              style={{ cursor: 'pointer' }}
            />
          ))
        )}

        {/* Category separators */}
        {(() => {
          let lastCategory = null
          const separators = []
          featureNames.forEach((feature, j) => {
            const category = FEATURE_META[feature].category
            if (lastCategory && category !== lastCategory) {
              separators.push(j)
            }
            lastCategory = category
          })
          return separators.map(j => (
            <line
              key={`sep-${j}`}
              x1={margin.left + j * cellSize - 0.5}
              y1={margin.top}
              x2={margin.left + j * cellSize - 0.5}
              y2={margin.top + polos.length * cellSize}
              stroke="#333"
              strokeWidth={2}
            />
          ))
        })()}
      </svg>

      {/* Tooltip */}
      {hoveredCell && (
        <div
          className="fixed bg-white border rounded shadow-sm p-2"
          style={{
            left: margin.left + hoveredCell.col * cellSize + 200,
            top: margin.top + hoveredCell.row * cellSize + 100,
            zIndex: 1050,
            fontSize: '0.8rem',
            pointerEvents: 'none'
          }}
        >
          <div className="font-bold">{hoveredCell.polo.nome}</div>
          <div className="text-gray-500">{FEATURE_META[hoveredCell.feature].label}</div>
          <div>z-score: {hoveredCell.value.toFixed(2)}</div>
        </div>
      )}

      </div>

      {/* Legend */}
      <div className="flex justify-between items-center mt-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Escala de cores:</span>
          <span className="text-sm">Abaixo da média</span>
          <svg width={100} height={12}>
            <defs>
              <linearGradient id="heatmap-gradient">
                <stop offset="0%" stopColor={colorScale(-2)} />
                <stop offset="50%" stopColor={colorScale(0)} />
                <stop offset="100%" stopColor={colorScale(2)} />
              </linearGradient>
            </defs>
            <rect width={100} height={12} fill="url(#heatmap-gradient)" rx={2} />
          </svg>
          <span className="text-sm">Acima da média</span>
        </div>
        <div className="flex gap-2">
          {Array.from({ length: k }).map((_, i) => (
            <div key={i} className="flex items-center gap-1 text-sm">
              <span
                className="rounded"
                style={{ width: 12, height: 12, backgroundColor: CLUSTER_COLORS[i] }}
              />
              Grupo {i + 1}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Generate interpretive axis label from loadings
 */
function generateAxisLabel(loadingsList) {
  const positive = loadingsList.filter(l => l.loading > 0).slice(0, 2)
  const negative = loadingsList.filter(l => l.loading < 0).slice(0, 2)

  const parts = []
  if (negative.length > 0) {
    parts.push(negative.map(l => FEATURE_META[l.feature].label).join(', '))
  }
  if (positive.length > 0) {
    parts.push(positive.map(l => FEATURE_META[l.feature].label).join(', '))
  }

  return parts.length === 2 ? `${parts[0]} ← → ${parts[1]}` : parts[0] || ''
}

/**
 * PCA Scatter Plot View
 */
const PCAScatter = ({ polos, projected, assignments, loadings, k }) => {
  const [hoveredPolo, setHoveredPolo] = useState(null)

  const padding = 50
  const width = 500
  const height = 400

  // Generate interpretive labels
  const xAxisLabel = generateAxisLabel(loadings[0])
  const yAxisLabel = generateAxisLabel(loadings[1])

  // Intro text
  const introText = (
    <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded px-3 py-2 text-sm mb-3">
      <i className="fa fa-lightbulb-o mr-2" />
      <strong>Como ler:</strong> Cada ponto é um polo. Polos próximos têm características similares.
      <br />
      <small className="text-gray-500">
        <i className="fa fa-info-circle mr-1" />
        Os grupos são calculados usando todas as {CLUSTERING_FEATURES.length} características,
        mas o gráfico mostra apenas 2 dimensões. Por isso, alguns pontos de grupos diferentes podem aparecer próximos —
        eles diferem em características não visíveis nesta projeção.
      </small>
    </div>
  )

  const xValues = projected.map(p => p[0])
  const yValues = projected.map(p => p[1])

  const xMin = Math.min(...xValues)
  const xMax = Math.max(...xValues)
  const yMin = Math.min(...yValues)
  const yMax = Math.max(...yValues)

  const xPadding = (xMax - xMin) * 0.1 || 1
  const yPadding = (yMax - yMin) * 0.1 || 1

  const scaleX = scaleLinear()
    .domain([xMin - xPadding, xMax + xPadding])
    .range([padding, width - padding])

  const scaleY = scaleLinear()
    .domain([yMin - yPadding, yMax + yPadding])
    .range([height - padding, padding])

  return (
    <div>
      {introText}
      <svg width={width} height={height} className="block mx-auto">
        {/* Grid */}
        {scaleX.ticks(5).map(t => (
          <line
            key={`vgrid-${t}`}
            x1={scaleX(t)}
            y1={padding}
            x2={scaleX(t)}
            y2={height - padding}
            stroke="#eee"
          />
        ))}
        {scaleY.ticks(5).map(t => (
          <line
            key={`hgrid-${t}`}
            x1={padding}
            y1={scaleY(t)}
            x2={width - padding}
            y2={scaleY(t)}
            stroke="#eee"
          />
        ))}

        {/* Axes */}
        <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#333" />
        <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="#333" />

        {/* Points */}
        {projected.map((point, i) => (
          <circle
            key={polos[i].id}
            cx={scaleX(point[0])}
            cy={scaleY(point[1])}
            r={hoveredPolo?.id === polos[i].id ? 10 : 7}
            fill={CLUSTER_COLORS[assignments[i] % CLUSTER_COLORS.length]}
            fillOpacity={hoveredPolo?.id === polos[i].id ? 1 : 0.8}
            stroke={hoveredPolo?.id === polos[i].id ? '#333' : 'white'}
            strokeWidth={hoveredPolo?.id === polos[i].id ? 2 : 1}
            style={{ cursor: 'pointer', transition: 'all 0.15s ease' }}
            onMouseEnter={() => setHoveredPolo(polos[i])}
            onMouseLeave={() => setHoveredPolo(null)}
          />
        ))}

        {/* Hover label */}
        {hoveredPolo && (() => {
          const idx = polos.findIndex(p => p.id === hoveredPolo.id)
          const point = projected[idx]
          return (
            <g>
              <rect
                x={scaleX(point[0]) - 4}
                y={scaleY(point[1]) - 32}
                width={Math.max(hoveredPolo.nome.length * 7 + 12, 80)}
                height={22}
                rx={4}
                fill="white"
                stroke="#333"
              />
              <text
                x={scaleX(point[0]) + 4}
                y={scaleY(point[1]) - 17}
                fontSize={11}
                fontWeight={600}
                fill="#333"
              >
                {hoveredPolo.nome}
              </text>
            </g>
          )
        })()}

        {/* Axis labels - interpretive */}
        <text x={width / 2} y={height - 5} textAnchor="middle" fontSize={10} fill="#666">
          {xAxisLabel}
        </text>
        <text
          x={12}
          y={height / 2}
          textAnchor="middle"
          fontSize={10}
          fill="#666"
          transform={`rotate(-90, 12, ${height / 2})`}
        >
          {yAxisLabel}
        </text>
      </svg>

      {/* Loadings interpretation */}
      <div className="grid grid-cols-2 mt-2 gap-2">
        <div>
          <div className="text-sm bg-gray-100 rounded p-2">
            <strong className="block mb-1">
              <i className="fa fa-arrows-h mr-1" />
              Eixo horizontal:
            </strong>
            <div className="flex flex-wrap gap-1">
              {loadings[0].slice(0, 4).map((l, i) => (
                <span key={i} className="inline-flex items-center px-1 py-0.5 rounded text-xs font-medium text-white" style={{
                  backgroundColor: l.loading > 0 ? '#28a745' : '#dc3545',
                  opacity: 0.7 + l.absLoading * 0.3,
                  fontSize: '0.65rem'
                }}>
                  {l.loading > 0 ? '→ ' : '← '}{FEATURE_META[l.feature].label}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div>
          <div className="text-sm bg-gray-100 rounded p-2">
            <strong className="block mb-1">
              <i className="fa fa-arrows-v mr-1" />
              Eixo vertical:
            </strong>
            <div className="flex flex-wrap gap-1">
              {loadings[1].slice(0, 4).map((l, i) => (
                <span key={i} className="inline-flex items-center px-1 py-0.5 rounded text-xs font-medium text-white" style={{
                  backgroundColor: l.loading > 0 ? '#28a745' : '#dc3545',
                  opacity: 0.7 + l.absLoading * 0.3,
                  fontSize: '0.65rem'
                }}>
                  {l.loading > 0 ? '↑ ' : '↓ '}{FEATURE_META[l.feature].label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex justify-center gap-3 mt-2">
        {Array.from({ length: k }).map((_, i) => (
          <div key={i} className="flex items-center gap-1 text-sm">
            <span
              className="rounded-full"
              style={{ width: 12, height: 12, backgroundColor: CLUSTER_COLORS[i] }}
            />
            Grupo {i + 1}
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Cluster Profiles View (Bar Chart comparison)
 */
const ClusterProfiles = ({ profiles, clusterLabels, clusterCounts, k }) => {
  const [selectedFeatures, setSelectedFeatures] = useState(() => {
    // Start with a subset of interesting features
    return [
      'fachada_ativa_pct',
      'arborizacao_pct',
      'muro_pct',
      'fim_de_semana_pct',
      'estacionamento_meio_fio_pct',
      'faixa_livre_pedestres_pct'
    ]
  })

  // Intro text
  const introText = (
    <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded px-3 py-2 text-sm mb-3">
      <i className="fa fa-lightbulb-o mr-2" />
      <strong>Como ler:</strong> Cada barra mostra a média de uma característica para o grupo.
      Compare os grupos lado a lado para entender o que os diferencia.
      Use os checkboxes à direita para explorar outras características.
    </div>
  )

  const barHeight = 18
  const barGap = 3
  const clusterGap = 12
  const labelWidth = 110
  const chartWidth = 300
  const margin = { top: 20, left: labelWidth, right: 60, bottom: 10 }

  const height = margin.top + selectedFeatures.length * (k * (barHeight + barGap) + clusterGap) + margin.bottom

  const maxValue = Math.max(
    ...profiles.flatMap(profile =>
      selectedFeatures.map(f => profile[CLUSTERING_FEATURES.indexOf(f)])
    )
  )

  const scaleX = scaleLinear()
    .domain([0, Math.max(maxValue, 100)])
    .range([0, chartWidth])

  const toggleFeature = (feature) => {
    setSelectedFeatures(prev =>
      prev.includes(feature)
        ? prev.filter(f => f !== feature)
        : [...prev, feature]
    )
  }

  return (
    <div>
      {introText}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <svg width={margin.left + chartWidth + margin.right} height={height}>
          {/* Feature groups */}
          {selectedFeatures.map((feature, fi) => {
            const featureIdx = CLUSTERING_FEATURES.indexOf(feature)
            const meta = FEATURE_META[feature]
            const yBase = margin.top + fi * (k * (barHeight + barGap) + clusterGap)

            return (
              <g key={feature}>
                {/* Feature label */}
                <text
                  x={margin.left - 8}
                  y={yBase + (k * (barHeight + barGap)) / 2}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fontSize={10}
                  fill={CATEGORY_COLORS[meta.category]}
                  fontWeight={500}
                >
                  {meta.label}
                </text>

                {/* Bars for each cluster */}
                {profiles.map((profile, ci) => {
                  const value = profile[featureIdx]
                  const y = yBase + ci * (barHeight + barGap)

                  return (
                    <g key={`bar-${fi}-${ci}`}>
                      <rect
                        x={margin.left}
                        y={y}
                        width={scaleX(value)}
                        height={barHeight}
                        fill={CLUSTER_COLORS[ci]}
                        rx={3}
                      />
                      <text
                        x={margin.left + scaleX(value) + 4}
                        y={y + barHeight / 2}
                        dominantBaseline="middle"
                        fontSize={9}
                        fill="#666"
                      >
                        {value.toFixed(0)}%
                      </text>
                    </g>
                  )
                })}

                {/* Separator line */}
                {fi < selectedFeatures.length - 1 && (
                  <line
                    x1={margin.left}
                    y1={yBase + k * (barHeight + barGap) + clusterGap / 2}
                    x2={margin.left + chartWidth}
                    y2={yBase + k * (barHeight + barGap) + clusterGap / 2}
                    stroke="#eee"
                  />
                )}
              </g>
            )
          })}
        </svg>

      {/* Legend */}
      <div className="flex justify-center gap-3 mt-2">
        {Array.from({ length: k }).map((_, i) => (
          <div key={i} className="flex items-center gap-1 text-sm">
            <span
              className="rounded"
              style={{ width: 12, height: 12, backgroundColor: CLUSTER_COLORS[i] }}
            />
            Grupo {i + 1} ({clusterCounts[i]})
          </div>
        ))}
      </div>
    </div>

    <div>
      <div className="bg-gray-100 rounded p-2">
        <h6 className="text-sm mb-2 font-semibold">Selecionar características:</h6>
        <div style={{ maxHeight: 280, overflowY: 'auto', fontSize: '0.75rem' }}>
          {Object.entries(
            CLUSTERING_FEATURES.reduce((acc, f) => {
              const cat = FEATURE_META[f].category
              if (!acc[cat]) acc[cat] = []
              acc[cat].push(f)
              return acc
            }, {})
          ).map(([category, features]) => (
            <div key={category} className="mb-2">
              <div
                className="text-sm font-bold mb-1"
                style={{ color: CATEGORY_COLORS[category] }}
              >
                {category === 'connectivity' ? 'Conectividade' :
                  category === 'diversity' ? 'Diversidade' : 'Conforto'}
              </div>
              {features.map(f => (
                <div key={f} className="py-0">
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-3 h-3 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      checked={selectedFeatures.includes(f)}
                      onChange={() => toggleFeature(f)}
                    />
                    <span className="ml-1" style={{ fontSize: '0.7rem' }}>
                      {FEATURE_META[f].label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Main Polos Clustering Component - All views on one page
 */
export default function PolosClustering({ polos, nivelFilter, canView = false }) {
  const [k, setK] = useState(3)

  // Filter polos
  const filteredPolos = useMemo(() => {
    if (!nivelFilter || nivelFilter.size === 0) return polos
    return polos.filter(p => nivelFilter.has(p.nivel))
  }, [polos, nivelFilter])

  // Extract and normalize features
  const { matrix, featureNames } = useMemo(() => {
    return extractFeatureMatrix(filteredPolos)
  }, [filteredPolos])

  const { normalized, stats } = useMemo(() => {
    return normalizeFeatures(matrix)
  }, [matrix])

  // Hierarchical clustering with Ward's linkage
  const hierarchical = useMemo(() => {
    if (normalized.length < 2) return { getAssignments: () => [0], order: [0] }
    const distMatrix = computeDistanceMatrix(normalized)
    return hierarchicalClustering(distMatrix)
  }, [normalized])

  const dendrogramOrder = hierarchical.order

  // Cluster assignments (cut tree at k)
  const assignments = useMemo(() => {
    return hierarchical.getAssignments(k)
  }, [hierarchical, k])

  // PCA for scatter plot
  const pca = useMemo(() => {
    if (normalized.length < 3) return null
    return computePCA(normalized)
  }, [normalized])

  // Cluster profiles
  const profiles = useMemo(() => {
    if (assignments.length === 0) return []
    return computeClusterProfiles(normalized, assignments, stats, k)
  }, [normalized, assignments, stats, k])

  // Global means for comparison
  const globalMeans = useMemo(() => {
    return stats.map(s => s.mean)
  }, [stats])

  // Cluster labels
  const clusterLabels = useMemo(() => {
    return generateClusterLabels(profiles, globalMeans)
  }, [profiles, globalMeans])

  // Cluster counts
  const clusterCounts = useMemo(() => {
    const counts = Array(k).fill(0)
    assignments.forEach(a => counts[a]++)
    return counts
  }, [assignments, k])

  if (filteredPolos.length < 3) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded px-4 py-3">
        <i className="fa fa-exclamation-triangle mr-2" />
        Selecione pelo menos 3 polos para análise de clusters.
      </div>
    )
  }

  const maxK = Math.min(8, Math.floor(filteredPolos.length / 2))

  return (
    <div className="polos-clustering">
      {/* Controls */}
      <div className="flex justify-between items-center mb-3">
        <div className="text-sm text-gray-500">
          <i className="fa fa-info-circle mr-1" />
          Agrupamento hierárquico (Ward) com {CLUSTERING_FEATURES.length} características brutas
        </div>
        <ClusterSelector k={k} onChange={setK} maxK={maxK} />
      </div>

      {/* Cluster Summary Cards - Full width, shows ALL polos */}
      <ClusterSummary
        profiles={profiles}
        globalMeans={globalMeans}
        featureNames={featureNames}
        clusterCounts={clusterCounts}
        polos={filteredPolos}
        assignments={assignments}
        canView={canView}
      />

      {/* All views in a grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* PCA Scatter - left side */}
        <div>
          <div className="border rounded p-3 h-full">
            <h6 className="mb-3 font-semibold">
              <i className="fa fa-dot-circle-o mr-2 text-gray-500" />
              Mapa de Posicionamento (PCA)
            </h6>
            {pca && (
              <PCAScatter
                polos={filteredPolos}
                projected={pca.projected}
                assignments={assignments}
                loadings={pca.loadings}
                k={k}
              />
            )}
          </div>
        </div>

        {/* Cluster Profiles - right side */}
        <div>
          <div className="border rounded p-3 h-full">
            <h6 className="mb-3 font-semibold">
              <i className="fa fa-bar-chart mr-2 text-gray-500" />
              Comparar Grupos
            </h6>
            <ClusterProfiles
              profiles={profiles}
              clusterLabels={clusterLabels}
              clusterCounts={clusterCounts}
              k={k}
            />
          </div>
        </div>

        {/* Heatmap - full width */}
        <div className="md:col-span-2">
          <div className="border rounded p-3">
            <h6 className="mb-3 font-semibold">
              <i className="fa fa-th mr-2 text-gray-500" />
              Matriz de Similaridade
            </h6>
            <DendrogramHeatmap
              polos={filteredPolos}
              normalizedData={normalized}
              assignments={assignments}
              order={dendrogramOrder}
              featureNames={featureNames}
              k={k}
            />
          </div>
        </div>
      </div>

      {/* Info footer */}
      <div className="bg-gray-50 border border-gray-200 rounded px-4 py-3 text-sm mt-4">
        <i className="fa fa-info-circle mr-2 text-blue-500" />
        <strong>Método:</strong> Agrupamento hierárquico com critério de Ward — minimiza a variância interna dos grupos,
        produzindo clusters mais equilibrados. Os resultados são determinísticos (mesmos dados = mesmos resultados).
        Usamos as {CLUSTERING_FEATURES.length} características brutas dos polos, não os pesos do IQUP.
      </div>
    </div>
  )
}
